import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import htmlToPdfmake from 'html-to-pdfmake';
import { Subject } from 'rxjs';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { PDFDocument, rgb } from 'pdf-lib';
import { behaviorData } from '../print-options/print-bip.interface';
import {
  TemplateField,
  TemplateSection,
} from '../../../../bip-template/bip-template.interface';
import { ChunkPipe } from '../../../../../core/pipes/chunk.pipe';
import { LabelKey } from '../../../../../core/models/report-labels.model';
import { ReportLabelsService } from '../../../../manage-settings/report-labels/report-labels.service';
import { ReportLabelPipe } from '../../../../../core/pipes/report-label.pipe';
import { UserDatePipe } from '../../../../../shared/pipes/user-date.pipe';
import { APP_LOOKUP_IDS } from './behavior-section-and-lookup-mapping.constant';
import { BehaviorDetailModel } from '../../../insights.interface';
import { CommonService } from '../../../../../shared/services/common.service';

@Component({
  selector: 'app-print-bip',
  standalone: true,
  imports: [
    DatePipe,
    CommonModule,
    FormsModule,
    ChunkPipe,
    ReportLabelPipe,
    UserDatePipe,
  ],
  templateUrl: './print-bip.component.html',
})
export class PrintBipComponent implements OnDestroy {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  printData: any = null;
  signatures = new Array(10);
  /* eslint-disable @typescript-eslint/no-explicit-any */
  @Input() set printOptions(val: any) {
    this.printData = val;
    this.cd.detectChanges();
    this.initialized = true;
  }
  @ViewChild('pdfContent', { static: true }) pdfContent!: ElementRef;
  initialized = false;
  private readonly destroy$ = new Subject<void>();

  base64 = '';
  bipLable: string = LabelKey.BIP;
  lookUp_Ids = APP_LOOKUP_IDS;

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly reportLabelsService: ReportLabelsService,
    private readonly commonService: CommonService
  ) {
    this.reportLabelsService.getLabelValue(LabelKey.BIP).subscribe(value => {
      this.bipLable = value;
    });
    (pdfMake.vfs as any) = pdfFonts;
  }

  getFrequencyAnswer(behavior: any) {
    let string = '';
    if (behavior.behaviourFrequencyId) {
      string = `It usually occurs at least once and possibly several times ${behavior.behaviourFrequencyName}.`;
    }
    if (behavior.behaviourFrequencyCount) {
      string = `${string} On average ${behavior.behaviourFrequencyCount} time(s)`;
    }
    string =
      !!behavior.behaviourFrequencyCount && !!behavior.duration
        ? `${string} for ${behavior.duration} minute(s).`
        : `${string}.`;

    return string;
  }

  getQuestionAnswer(behavior: BehaviorDetailModel, questionId: number) {
    console.log('behavior: ', behavior);
    console.log('questionId: ', questionId);
    return behavior?.caseBehaviorCustomQuestionAnswer?.find(
      answer => answer.customQuestionId === questionId
    );
  }

  isDataAvailable(section: TemplateSection, behavior: behaviorData) {
    if (section.id === 1) {
      return behavior && section.isSelected;
    } else if (section.id === 2) {
      return behavior.caseBehaviorStrategies.length > 0 && section.isSelected;
    } else if (section.id === 3) {
      return (
        behavior.caseBehvaiorCustomQuestionIds &&
        behavior?.caseBehvaiorCustomQuestionIds?.length > 0 &&
        section.isSelected
      );
    } else {
      return (
        behavior.graphImages &&
        behavior.graphImages.length > 0 &&
        section.isSelected
      );
    }
  }

  getFieldValue(fieldName: string): any {
    const caseData = this.printData?.caseData;
    switch (fieldName) {
      case 'Student Date of Birth':
        return caseData?.student?.dateOfBirth
          ? this.formatDate(caseData.student.dateOfBirth)
          : '-';
      case 'Student School':
        return caseData?.student?.organizationName || '-';
      case 'Safety Plan':
        return caseData?.isEnabledSafetyPlan ? 'Yes' : 'No';
      case 'Behavior Screener':
        return caseData?.behaviorScreenerDate
          ? this.formatDate(caseData.behaviorScreenerDate)
          : '-';
      case 'Plan Created':
        return caseData?.planCreatedDate
          ? this.formatDate(caseData.planCreatedDate)
          : '-';
      case 'Last Revised':
        return caseData?.lastRevisedDate
          ? this.formatDate(caseData.lastRevisedDate)
          : '-';
      default:
        return '-';
    }
  }
  getSelectedFields(): any[] {
    return (
      this.printData?.bipTemplate?.templateFields.filter(
        (field: TemplateField) =>
          field.isSelected && field.fieldName !== 'Student Age'
      ) || []
    );
  }

  chunkArray(array: any[], size: number): any[][] {
    return array.reduce(
      (acc, _, i) => (i % size ? acc : [...acc, array.slice(i, i + size)]),
      []
    );
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US');
  }
  async printPDF() {
    const pdfContent = document.getElementById('pdf-data');

    if (pdfContent) {
      const pdfMakeContent = htmlToPdfmake(pdfContent.innerHTML);
      const pdfData = this.printData;

      const logoBase64 = pdfData?.organizationDetail?.logoUrl
        ? await this.commonService.getBase64ImageFromUrl(
            pdfData.organizationDetail.logoUrl
          )
        : await this.commonService.getBase64ImageFromUrl(
            '/assets/images/logo.png'
          );

      const columns: any[] = [
        logoBase64
          ? {
              image: logoBase64,
              width: 125,
              fit: [125, 40], // Fit the image within 125x40 dimensions (reduces height)
              alignment: 'left',
            }
          : {},
        {
          stack: [
            `${pdfData?.organizationDetail?.address || ''}, ${
              pdfData?.organizationDetail?.city || ''
            }`,
            `${pdfData?.organizationDetail?.state || ''}, ${
              pdfData?.organizationDetail?.country || ''
            }, ${pdfData?.organizationDetail?.zipCode || ''}`,
          ],
          fontSize: 10,
          alignment: 'right',
        },
      ];

      const waterMarkOption = {
        text: 'DRAFT',
        color: 'gray',
        opacity: 0.2,
      };
      // Define the PDF document
      const documentDefinition: TDocumentDefinitions = {
        content: pdfMakeContent,
        pageMargins: [20, 60, 20, 40],
        header: (currentPage, pageCount, pageSize) => {
          const pageWidth =
            typeof pageSize === 'object' ? pageSize.width : 595.28;
          const lineWidth = 552;

          return [
            {
              margin: [20, 10, 20, 0],
              columns: columns as any,
            },
            {
              canvas: [
                {
                  type: 'line',
                  x1: (pageWidth - lineWidth) / 2,
                  y1: 0,
                  x2: (pageWidth - lineWidth) / 2 + lineWidth,
                  y2: 0,
                  lineWidth: 1,
                  lineColor: '#eaecf0',
                },
              ],
              margin: [0, 5, 0, 8],
            },
          ];
        },

        footer: function (currentPage: number) {
          return {
            columns: [
              { text: '', alignment: 'left' },
              {
                text: `Powered by Insights to Behavior®`,
                alignment: 'center',
              },
              { text: `${currentPage}`, alignment: 'right' },
            ],
            margin: [10, 20, 10, 0],
            color: '#475467',
          };
        },
        watermark: pdfData.PrintOptions.isDraft
          ? waterMarkOption
          : ({} as { text: string; color: string; opacity: number }),
        styles: {
          desc: {
            fontSize: 12,
          },
          address: {
            fontSize: 12,
          },
          title: {
            fontSize: 12,
          },
          datalabel: {
            lineHeight: 1.5,
            color: '#475467',
          },
          commentLabel: {
            lineHeight: 1.3,
            color: '#475467',
            fontSize: 9,
          },
          extraLineHeight: {
            lineHeight: 1.5,
          },
          font10: {
            fontSize: 12,
            color: '#475467',
          },
          dataValue: {
            color: '#475467',
          },
          columns: {
            columnGap: 10,
          },
        },
        defaultStyle: {
          columnGap: 10,
          fontSize: 12,
        },
      };

      const pdfBuffer = await new Promise<Uint8Array>(resolve => {
        pdfMake
          .createPdf(documentDefinition)
          .getBuffer((buffer: Uint8Array) => {
            resolve(buffer);
          });
      });
      const pdfUrls = this.printData.attachments;
      const pdfBuffers = await this.loadPdfsFromUrls(pdfUrls);

      const mergedPdfBytes = await this.mergeMultiplePDFs(
        pdfBuffer,
        pdfBuffers
      );

      this.downloadMergedPdf(mergedPdfBytes);
      this.initialized = false;
      this.ngOnDestroy();
    }
  }

  async loadPdf(url: string): Promise<Uint8Array> {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  async loadPdfsFromUrls(urls: string[]): Promise<Uint8Array[]> {
    const pdfBuffers: Uint8Array[] = [];
    for (const url of urls) {
      const arrayBuffer = await this.fetchPdf(url);
      pdfBuffers.push(new Uint8Array(arrayBuffer));
    }
    return pdfBuffers;
  }

  async fetchPdf(url: string): Promise<ArrayBuffer> {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return arrayBuffer;
  }

  readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () =>
        reject(new Error('Failed to read file as ArrayBuffer'));
      reader.readAsArrayBuffer(file);
    });
  }

  async mergePDFs(
    existingPdfBytes: Uint8Array,
    newPdfBytes: Uint8Array
  ): Promise<Uint8Array> {
    const existingPdfDoc = await PDFDocument.load(existingPdfBytes);
    const newPdfDoc = await PDFDocument.load(newPdfBytes);

    const copiedPages = await existingPdfDoc.copyPages(
      newPdfDoc,
      newPdfDoc.getPageIndices()
    );
    copiedPages.forEach(page => existingPdfDoc.addPage(page));

    const mergedPdfBytes = await existingPdfDoc.save();
    return mergedPdfBytes;
  }

  base64ToArrayBuffer(base64: string): ArrayBuffer {
    if (!base64) {
      throw new Error('Base64 string is empty or undefined');
    }
    base64 = base64.replace(/\s/g, '');
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    try {
      const binaryString = atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      return bytes.buffer;
    } catch (error) {
      console.error('Error decoding Base64:', error);
      throw new Error('Invalid Base64 format');
    }
  }

  async mergeMultiplePDFs(
    existingPdfBytes: Uint8Array,
    pdfBuffers: Uint8Array[]
  ): Promise<Uint8Array> {
    const mergedPdfDoc = await PDFDocument.load(existingPdfBytes);
    const pageCount = mergedPdfDoc.getPageCount();
    for (const pdfBuffer of pdfBuffers) {
      const pdfDoc = await PDFDocument.load(pdfBuffer);
      const copiedPages = await mergedPdfDoc.copyPages(
        pdfDoc,
        pdfDoc.getPageIndices()
      );

      copiedPages.forEach(async (page, index) => {
        page.setSize(595.28, 841.89);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { width, height } = page.getSize();
        const fontSize = 10;
        const text = `Page ${pageCount + index + 1}`;
        page.drawText(text, {
          x: width / 2 - fontSize * 2,
          y: 10,
          size: fontSize,
          color: rgb(0, 0, 0),
        });
      });
      copiedPages.forEach(page => mergedPdfDoc.addPage(page));
    }

    const mergedPdfBytes = await mergedPdfDoc.save();
    return mergedPdfBytes;
  }

  downloadMergedPdf(pdfBytes: Uint8Array) {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Plan_${this.printData?.caseData?.student?.firstName}_${this.printData?.caseData?.student?.lastName}.pdf`;
    link.click();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
